import { clsx } from 'clsx';
import { useEffect } from 'react';
import { useLocale } from 'remix-i18next/react';
import { Icon } from '#common/components/Icon.tsx';
import { type TranslateFunc, type TranslateRawFunc } from '#common/i18n/edit-mode.tsx';
import { useAnonymousRegistrationController } from '#speaker-shop/hooks/useAnonymousRegistrationController.ts';
import { type CoreTheme } from '#speaker-shop/hooks/useCoreTheme.ts';
import { Button } from './Button.tsx';
import { Dialog } from './Dialog.tsx';

interface LimitOverlayProps {
    t: TranslateFunc;
    rawT: TranslateRawFunc;
    onClose?: () => void;
    onVerified?: () => void;
    initialTitle?: string;
    initialMessage?: string;
    /** When true, renders as a full-page modal that can be closed. Default: false (inline overlay) */
    isModal?: boolean;
    appearance?: 'legacy' | 'unified';
    theme?: CoreTheme;
}

export function LimitReachedOverlay({
    t,
    rawT,
    onClose,
    onVerified,
    initialTitle,
    initialMessage,
    isModal = false,
    appearance = 'legacy',
    theme = 'dark',
}: LimitOverlayProps) {
    const unified = appearance === 'unified';
    const locale = useLocale();
    const {
        formState,
        email,
        code,
        marketingConsent,
        isSubmittingEmail,
        isSubmittingCode,
        fieldErrors,
        setEmail,
        setCode,
        setMarketingConsent,
        submitEmail,
        submitCode,
        reset,
    } = useAnonymousRegistrationController({
        rawT,
        locale,
        onVerified: () => {
            onVerified?.();
            onClose?.();
        }
    });

    useEffect(() => {
        return () => {
            reset();
        };
    }, [ reset ]);

    const title = formState === 'code' ? t('verify-email-title') : initialTitle || t('anonymous-limit-reached-title');

    const description = formState === 'code'
        ? t('verification-code-sent', { email })
        : initialMessage || t('anonymous-limit-reached-message');

    const formContent = (
        <div className='space-y-4 text-left'>
            {fieldErrors.form?.map(error => (
                <p key={error} className={clsx('text-sm', unified ? 'text-red-300' : 'text-red-600')}>{error}</p>
            ))}

            {formState === 'email' && (
                <>
                    <label className='block'>
                        <span className='sr-only'>{rawT('email')}</span>
                        <div className='flex h-full relative'>
                            <span
                                className={clsx(
                                    'p-3 border border-r-0',
                                    unified
                                        ? 'border-[var(--core-input-border)] bg-[var(--core-soft-bg)] text-[var(--core-text)] opacity-60'
                                        : 'border-gray-300 text-gray-500 bg-gray-50'
                                )}
                            >
                                <Icon icon='heroicons--envelope' />
                            </span>
                            <input
                                value={email}
                                onChange={event => setEmail(event.target.value)}
                                placeholder={rawT('email')}
                                type='email'
                                autoComplete='email'
                                className={clsx(
                                    'block w-full rounded-r-md border px-3 py-2 text-sm shadow-sm focus:outline-none',
                                    unified
                                        ? 'core-input'
                                        : 'border-gray-300 text-gray-900 focus:border-slate-500 focus:ring-1 focus:ring-slate-500'
                                )}
                            />
                        </div>
                        {fieldErrors.email?.map(error => (
                            <span
                                key={error}
                                className={clsx('mt-2 block text-sm', unified ? 'text-red-300' : 'text-red-600')}
                            >
                                {error}
                            </span>
                        ))}
                    </label>

                    <label className='flex items-start gap-3'>
                        <input
                            checked={marketingConsent}
                            onChange={event => setMarketingConsent(event.target.checked)}
                            type='checkbox'
                            className={clsx(
                                'mt-1 h-4 w-4 rounded',
                                unified
                                    ? 'border-[var(--core-input-border)] accent-[var(--core-blue)]'
                                    : 'border-gray-300 text-slate-900 focus:ring-slate-500'
                            )}
                        />
                        <span className={clsx('text-xs', unified ? 'core-booking-muted' : 'text-gray-700')}>
                            {t('marketing-consent')}
                        </span>
                    </label>
                    {fieldErrors.marketingConsent?.map(error => (
                        <p key={error} className={clsx('text-sm', unified ? 'text-red-300' : 'text-red-600')}>
                            {error}
                        </p>
                    ))}

                    {unified
                        ? (
                            <button
                                className='core-primary-button w-full'
                                type='button'
                                onClick={() => void submitEmail()}
                                disabled={isSubmittingEmail}
                            >
                                {isSubmittingEmail ? t('sending') : t('continue')}
                            </button>
                        )
                        : (
                            <Button
                                color='slate'
                                size='normal'
                                className='w-full'
                                type='button'
                                onClick={() => void submitEmail()}
                                disabled={isSubmittingEmail}
                            >
                                {isSubmittingEmail ? t('sending') : t('continue')}
                            </Button>
                        )}
                </>
            )}

            {formState === 'code' && (
                <>
                    <label className='block'>
                        <span className='sr-only'>{rawT('verification-code')}</span>
                        <div className='flex h-full relative'>
                            <span
                                className={clsx(
                                    'p-3 border border-r-0',
                                    unified
                                        ? 'border-[var(--core-input-border)] bg-[var(--core-soft-bg)] text-[var(--core-text)] opacity-60'
                                        : 'border-gray-300 text-gray-500 bg-gray-50'
                                )}
                            >
                                <Icon icon='heroicons--key' />
                            </span>
                            <input
                                value={code}
                                onChange={event => setCode(event.target.value)}
                                placeholder={rawT('verification-code')}
                                type='text'
                                autoComplete='one-time-code'
                                className={clsx(
                                    'block w-full rounded-r-md border px-3 py-2 text-sm shadow-sm focus:outline-none',
                                    unified
                                        ? 'core-input'
                                        : 'border-gray-300 text-gray-900 focus:border-slate-500 focus:ring-1 focus:ring-slate-500'
                                )}
                            />
                        </div>
                        {fieldErrors.code?.map(error => (
                            <span
                                key={error}
                                className={clsx('mt-2 block text-sm', unified ? 'text-red-300' : 'text-red-600')}
                            >
                                {error}
                            </span>
                        ))}
                    </label>

                    {unified
                        ? (
                            <button
                                className='core-primary-button w-full'
                                type='button'
                                onClick={() => void submitCode()}
                                disabled={isSubmittingCode}
                            >
                                {isSubmittingCode ? t('verifying') : t('verify')}
                            </button>
                        )
                        : (
                            <Button
                                color='slate'
                                size='normal'
                                className='w-full'
                                type='button'
                                onClick={() => void submitCode()}
                                disabled={isSubmittingCode}
                            >
                                {isSubmittingCode ? t('verifying') : t('verify')}
                            </Button>
                        )}
                </>
            )}
        </div>
    );

    const inlineOverlay = (
        <div
            className={clsx(
                'h-full backdrop-blur-xs flex items-center justify-center p-2 md:p-6 transition-opacity duration-200 z-20',
                unified ? 'core-booking-theme core-generation-overlay' : 'bg-black/60'
            )}
            data-theme={unified ? theme : undefined}
        >
            <div
                className={clsx(
                    'max-w-md w-full mx-auto',
                    unified ? 'core-generation-panel p-5 sm:p-6' : 'bg-white rounded-lg shadow-2xl p-4'
                )}
            >
                <div className='text-center'>
                    <div
                        className={clsx(
                            'w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4',
                            unified ? 'bg-[color-mix(in_srgb,var(--core-accent)_16%,transparent)]' : 'bg-orange-100'
                        )}
                    >
                        <Icon
                            icon='heroicons--clock'
                            className={clsx('text-3xl', unified ? 'text-[var(--core-accent)]' : 'text-orange-600')}
                        />
                    </div>
                    <h3
                        className={clsx(
                            'text-2xl font-bold mb-3',
                            unified ? 'text-[var(--core-text)]' : 'text-gray-900'
                        )}
                    >
                        {title}
                    </h3>
                    <p className={clsx('mb-4 leading-tight', unified ? 'core-booking-muted' : 'text-gray-600')}>
                        {description}
                    </p>
                    {formContent}
                </div>
            </div>
        </div>
    );

    return isModal
        ? (
            <Dialog
                open={true}
                onClose={onClose ?? (() => {})}
                title={title}
                description={description}
                icon='heroicons--clock'
                type='alert'
                showCloseIcon={!!onClose}
                appearance={appearance}
                theme={theme}
            >
                {formContent}
            </Dialog>
        )
        : inlineOverlay;
}
